// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { type TypographyVariant, styled } from '@mui/material/styles';
import * as React from 'react';
import { Link as RRLink } from 'react-router';
import type { Theme } from '../../theme/Theme';
import { Box, MUILink, Typography } from '../base';

function StyledRRLink({
  children,
  to,
  onClick,
  onMouseOver,
}: {
  readonly children: React.ReactNode;
  readonly to: string;
  readonly onClick?: () => void;
  readonly onMouseOver?: () => void;
}) {
  return (
    <Box
      component={RRLink}
      to={to}
      onClick={onClick}
      onMouseOver={onMouseOver}
      sx={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        color: (theme: Theme) => theme.palette.secondary.main,
        fontWeight: (theme: Theme) => theme.typography.fontWeightRegular,
        textDecoration: 'none',
        '&:hover': {
          color: (theme: Theme) => theme.palette.secondary.dark,
          textDecoration: 'underline',
        },
      }}
    >
      {children}
    </Box>
  );
}

const StyledTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'white' && prop !== 'strike' && prop !== 'sx',
})<{
  readonly white?: boolean;
  readonly strike?: boolean;
  readonly component?: React.ElementType;
}>(({ theme, white, strike }) => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: white ? theme.custom.colors.common.white : theme.palette.secondary.main,
  fontWeight: theme.typography.fontWeightRegular,
  textDecoration: white ? (strike ? 'underline line-through' : 'underline') : strike ? 'line-through' : 'none',
  '&:hover': {
    color: white ? theme.custom.colors.common.darkWhite : theme.palette.secondary.dark,
    textDecoration: strike ? 'underline line-through' : 'underline',
  },
}));

const StyledMUILink = styled(MUILink, {
  shouldForwardProp: (prop) => prop !== 'white' && prop !== 'strike' && prop !== 'sx',
})<{
  readonly white?: boolean;
  readonly strike?: boolean;
  readonly component?: React.ElementType;
}>(({ theme, white, strike }) => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: white ? theme.custom.colors.common.white : theme.palette.secondary.main,
  fontWeight: theme.typography.fontWeightRegular,
  textDecoration: white ? (strike ? 'underline line-through' : 'underline') : strike ? 'line-through' : 'none',
  '&:hover': {
    color: white ? theme.custom.colors.common.darkWhite : theme.palette.secondary.dark,
    textDecoration: strike ? 'underline line-through' : 'underline',
  },
}));

interface Props {
  readonly path: string;
  readonly title: string | React.ReactElement<{ readonly children?: React.ReactNode }>;
  readonly variant?: TypographyVariant;
  readonly component?: React.ElementType;
  readonly white?: boolean;
  readonly absolute?: boolean;
  readonly newTab?: boolean;
  readonly onClick?: () => void;
  readonly strike?: boolean;
  readonly inlineText?: boolean;
  readonly onMouseOver?: () => void;
}
export function Link({
  inlineText,
  path,
  title,
  variant: variantIn,
  component,
  white,
  absolute,
  newTab,
  onClick,
  strike,
  onMouseOver,
}: Props) {
  const variant = variantIn || 'body1';
  let linkText: React.ReactElement;
  if (typeof title === 'string') {
    if (inlineText) {
      // TODO: this whole block is janky
      if (absolute || path.startsWith('http') || newTab) {
        return (
          <StyledMUILink href={path} target={newTab ? '_blank' : undefined} onClick={onClick}>
            {title}
          </StyledMUILink>
        );
      }
      return (
        <StyledRRLink onMouseOver={onMouseOver} to={path} onClick={onClick}>
          {title}
        </StyledRRLink>
      );
    }
    linkText =
      component === undefined ? (
        <StyledTitle variant={variant} white={white} strike={strike}>
          {title}
        </StyledTitle>
      ) : (
        <StyledTitle variant={variant} white={white} component={component} strike={strike}>
          {title}
        </StyledTitle>
      );
  } else {
    linkText = React.cloneElement(
      title,
      {
        // TODO: this is missing styles
        ...title.props,
      },
      title.props.children,
    );
  }

  if (absolute || path.startsWith('http') || newTab) {
    return (
      <a
        style={{ textDecorationColor: '#9ec745' }}
        href={path}
        target={newTab ? '_blank' : undefined}
        onClick={onClick}
      >
        {linkText}
      </a>
    );
  }

  return (
    <StyledRRLink onMouseOver={onMouseOver} to={path} onClick={onClick}>
      {linkText}
    </StyledRRLink>
  );
}
